import { IonButton, IonLabel, IonToggle } from '@ionic/react'
import { minAcceptedHvacTemperature, normalHvacTemperature } from '@vae/server/src/constants'
import React, { useContext } from 'react'
import { useTranslation } from 'react-i18next'

import { logEvent } from '../../../../analytics/utils/firebaseUtils'
import useUser from '../../../../contexts/hooks/useUser'
import { trpc } from '../../../../modules/trpc'
import { HvacContext } from '../HvacContext'

const SmartHeatingToggle = () => {
	const { modeSwitchLoading } = useContext(HvacContext)

	const { user } = useUser()

	const { t } = useTranslation('pages_hvac', { keyPrefix: 'smartHeating' })

	const { data: userSettings, refetch } = trpc.user.userSettings.get.useQuery(undefined, {
		enabled: !!user?.id,
	})

	// Edit user settings mutation
	const { mutate: editUserSettings } = trpc.user.userSettings.edit.useMutation({
		onSuccess: () => {
			refetch()
		},
	})

	const isSmartHeatingEnabled = userSettings?.smartHeatingEnabled ?? false
	const isDisabled = !isSmartHeatingEnabled || modeSwitchLoading

	const handleToggle = () => {
		logEvent('toggle-smart-heating', {
			smartHeatingEnabled: !isSmartHeatingEnabled,
		})
		editUserSettings({
			userId: user?.id ?? 0,
			data: {
				smartHeatingEnabled: !isSmartHeatingEnabled,
			},
		})
	}

	return (
		<>
			<div
				className='heating relative border border-[#e5e5e5] dark:border-[#4a7c7a] bg-[#f9f9f9] dark:bg-[#144448] text-gray-800 dark:text-white rounded-lg overflow-hidden'
				id='Smart'
			>
				<div className='title flex items-center justify-between h-[48px] bg-white dark:bg-[#0d2d30] border-b border-[#e5e5e5] dark:border-[#4a7c7a] px-4'>
					<IonLabel>{t('title')}</IonLabel>
					<IonToggle
						checked={isSmartHeatingEnabled}
						onIonChange={handleToggle}
						disabled={modeSwitchLoading}
						className='[--track-background:#d6d6d6] dark:[--track-background:#09292b] [--handle-background:#a0a0a0] dark:[--handle-background:#063a3e] [--track-background-checked:#a8da9a] dark:[--track-background-checked:#0a5a5f] [--handle-background-checked:#52ae32] dark:[--handle-background-checked:#3cb49f]'
					/>
				</div>

				<div className={`p-4 ${!isSmartHeatingEnabled ? 'opacity-50' : ''}`}>
					<p
						className={`text-sm leading-relaxed ${
							!isSmartHeatingEnabled
								? 'text-gray-400 dark:text-gray-500'
								: 'text-gray-600 dark:text-gray-300'
						}`}
					>
						{t('description')}
					</p>
					<div className='temperature-settings mt-6 space-y-4'>
						<div className='flex flex-col space-y-2'>
							<div className='flex items-center justify-center space-x-4'>
								<IonButton
									fill='solid'
									className='w-12 h-12 --border-radius:8px m-0'
									style={{
										'--background': !isSmartHeatingEnabled ? '#d1d5db' : '#4a7c7a',
										'--background-hover': !isSmartHeatingEnabled ? '#d1d5db' : '#3a6b69',
										'--color': !isSmartHeatingEnabled ? '#9ca3af' : 'white',
										'--border-radius': '8px',
										width: '48px',
										height: '48px',
										margin: '0',
									}}
									onClick={() => {
										if (isSmartHeatingEnabled) {
											const newTemp = Math.max(
												minAcceptedHvacTemperature,
												(userSettings?.smartHeatingSavingTemperature ??
													minAcceptedHvacTemperature) - 1
											)
											editUserSettings({
												userId: user?.id ?? 0,
												data: { smartHeatingSavingTemperature: newTemp },
											})
										}
									}}
									disabled={
										isDisabled ||
										(userSettings?.smartHeatingSavingTemperature ?? minAcceptedHvacTemperature) <=
											minAcceptedHvacTemperature
									}
								>
									-
								</IonButton>
								<div className='flex items-center justify-center min-w-[80px]'>
									<span
										className={`text-3xl font-bold ${
											!isSmartHeatingEnabled
												? 'text-gray-400 dark:text-gray-500'
												: 'text-gray-800 dark:text-white'
										}`}
									>
										{userSettings?.smartHeatingSavingTemperature ?? minAcceptedHvacTemperature}
									</span>
									<span
										className={`text-lg ml-1 ${
											!isSmartHeatingEnabled
												? 'text-gray-400 dark:text-gray-500'
												: 'text-gray-600 dark:text-gray-300'
										}`}
									>
										°C
									</span>
								</div>
								<IonButton
									fill='solid'
									className='w-12 h-12 --border-radius:8px m-0'
									style={{
										'--background': !isSmartHeatingEnabled ? '#d1d5db' : '#4a7c7a',
										'--background-hover': !isSmartHeatingEnabled ? '#d1d5db' : '#3a6b69',
										'--color': !isSmartHeatingEnabled ? '#9ca3af' : 'white',
										'--border-radius': '8px',
										width: '48px',
										height: '48px',
										margin: '0',
									}}
									onClick={() => {
										if (
											isSmartHeatingEnabled &&
											(userSettings?.smartHeatingSavingTemperature ?? minAcceptedHvacTemperature) +
												1 !==
												(userSettings?.smartHeatingNormalTemperature ?? normalHvacTemperature) &&
											(userSettings?.smartHeatingSavingTemperature ?? minAcceptedHvacTemperature) <
												(userSettings?.smartHeatingNormalTemperature ?? normalHvacTemperature) - 1
										) {
											const newTemp = Math.min(
												(userSettings?.smartHeatingNormalTemperature ?? normalHvacTemperature) - 1,
												(userSettings?.smartHeatingSavingTemperature ??
													minAcceptedHvacTemperature) + 1
											)
											editUserSettings({
												userId: user?.id ?? 0,
												data: { smartHeatingSavingTemperature: newTemp },
											})
										}
									}}
									disabled={
										isDisabled ||
										(userSettings?.smartHeatingSavingTemperature ?? minAcceptedHvacTemperature) +
											1 ===
											(userSettings?.smartHeatingNormalTemperature ?? normalHvacTemperature) ||
										(userSettings?.smartHeatingSavingTemperature ?? minAcceptedHvacTemperature) >=
											(userSettings?.smartHeatingNormalTemperature ?? normalHvacTemperature) - 1
									}
								>
									+
								</IonButton>
							</div>
						</div>
					</div>
				</div>
			</div>
		</>
	)
}

export default SmartHeatingToggle
